import type { ApiObjectWithReferences } from "@/models/api";
import type { CustomField } from "@/models/custom-fields";
import type { CustomSchemaEntryOut } from "@/models/custom-schemas";
import type { SchemaEntryOut } from "@/models/field-schemas";
import { idToBarcode } from "@/helper/InternalBarcode";

// Password values are never matched, even though the field's own name still is — that
// keeps "find the field called Wifi Password" working without making search a way to
// probe for items by guessing their secret values.
function matchesCustomFields(fields: CustomField[] | undefined, searchLower: string): boolean {
  return (fields ?? []).some((field) => {
    if ((field.name || '').toLowerCase().indexOf(searchLower) !== -1) return true;
    if (field.field_type === 'password') return false;
    return (field.values ?? []).some((v) => typeof v.value === 'string' && v.value.toLowerCase().indexOf(searchLower) !== -1);
  });
}

type SearchableSchemaEntry = {
  name?: string | null;
  field_type?: string | null;
  values: SchemaEntryOut["values"] | CustomSchemaEntryOut["values"];
  sub_entries?: SearchableSchemaEntry[] | null;
};

function matchesSchemaEntries(entries: SearchableSchemaEntry[] | null | undefined, searchLower: string): boolean {
  return (entries ?? []).some((entry) => {
    if ((entry.name || '').toLowerCase().indexOf(searchLower) !== -1) return true;
    if (entry.field_type !== 'password') {
      const valueMatch = (entry.values ?? []).some(
        (v) => typeof v.value === 'string' && v.value.toLowerCase().indexOf(searchLower) !== -1
      );
      if (valueMatch) return true;
    }
    return matchesSchemaEntries(entry.sub_entries, searchLower);
  });
}

export function filterContainers(item: ApiObjectWithReferences, search: string): boolean {
  const searchUpper = search.toUpperCase()
  const searchLower = search.toLowerCase()
  const searchLowerNoSpace = searchLower.replaceAll(/\s/g, '')
  return !search || (
    item.object.name.toLowerCase().indexOf(searchLower) !== -1
    || String(item.object.id || '').toLowerCase().indexOf(searchLower) !== -1
    || (item.object.description || '').toLowerCase().indexOf(searchLower) !== -1
    || (item.object.url || '').toLowerCase().indexOf(searchLower) !== -1
    || (
      item.barcodes
        .map((barcode) => (barcode.code || '').replaceAll(/\s/g, '').toLowerCase().indexOf(searchLowerNoSpace) !== -1)
        .reduce((previousValue: boolean, currentValue: boolean) => previousValue || currentValue, false)
    )
    || (typeof item.object.id === 'number' && idToBarcode(item.object.id).indexOf(searchUpper) === 0)
    || matchesCustomFields(item.custom_fields, searchLower)
    || (item.schemas ?? []).some((schema) => matchesSchemaEntries(schema.entries, searchLower))
  )
}
